import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FileQuestion } from "lucide-react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import Reveal from "../components/ui/Reveal";
import SEO, { generateArticleSchema, generateBreadcrumbSchema } from "../components/seo/SEO";
import { APP_STORE_URL, PLAY_STORE_URL, trackStoreClick } from "../constants/appStores";
import { getLatestNews, getNewsById } from "../lib/news";

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cdefs%3E%3ClinearGradient id='grad' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%2343ad69;stop-opacity:0.1'/%3E%3Cstop offset='100%25' style='stop-color:%23094268;stop-opacity:0.1'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect fill='url(%23grad)' width='800' height='600'/%3E%3Ctext fill='%234a5d52' font-family='system-ui,sans-serif' font-size='24' font-weight='600' x='50%25' y='50%25' text-anchor='middle' dominant-baseline='middle'%3ECampusGenie%3C/text%3E%3C/svg%3E";

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-NG", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatReadTime(content) {
  if (!content) return "3 min read";
  const words = content.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
}

function ShareButton({ platform, url, title }) {
  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
  };

  const icons = {
    twitter: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    whatsapp: (
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
  };

  return (
    <a
      href={shareUrls[platform]}
      target="_blank"
      rel="noopener noreferrer"
      className={`blog-share-btn blog-share-${platform}`}
      title={`Share on ${platform}`}
    >
      {icons[platform]}
    </a>
  );
}

export default function BlogPostPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => {
    let active = true;

    async function loadArticle() {
      setLoading(true);
      setError("");

      try {
        const response = await getNewsById(id);
        const data = response.data ?? response;

        if (active) {
          setArticle(data);
        }
      } catch (requestError) {
        if (active) {
          setError(requestError.message || "Article not found.");
          setArticle(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    async function loadRelated() {
      try {
        const response = await getLatestNews();
        const list = Array.isArray(response.data) ? response.data : response.data?.data ?? [];
        if (active) {
          setRelated(list.filter((entry) => String(entry.id) !== String(id)).slice(0, 3));
        }
      } catch {
        if (active) {
          setRelated([]);
        }
      }
    }

    if (id) {
      loadArticle();
      loadRelated();
      window.scrollTo(0, 0);
    }

    return () => {
      active = false;
    };
  }, [id]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (loading) {
    return (
      <div className="blog-post-loading">
        <div className="blog-post-loading-spinner" />
        <p>Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <>
        <SEO
          title="Article Not Found"
          description="The article you're looking for doesn't exist or has been removed."
          url={`/blog/${id}`}
          noindex={true}
        />
        <div className="blog-post-error">
          <span className="blog-post-error-icon" aria-hidden>
            <FileQuestion size={56} strokeWidth={1.75} />
          </span>
          <h2>Article not found</h2>
          <p>{error || "The article you're looking for doesn't exist or has been removed."}</p>
          <button className="btn btn-primary" onClick={() => navigate("/blog")}>
            Back to Blog
          </button>
        </div>
      </>
    );
  }

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" },
    { name: article.title, url: `/blog/${id}` },
  ];

  const structuredData = [
    generateArticleSchema({
      id: article.id,
      title: article.title,
      excerpt: article.excerpt,
      image: article.image,
      publishedTime: article.created_at,
      modifiedTime: article.updated_at,
      tags: article.tags,
    }),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt || `Read ${article.title} on CampusGenie Blog.`}
        url={`/blog/${id}`}
        image={article.image}
        type="article"
        publishedTime={article.created_at}
        modifiedTime={article.updated_at}
        tags={article.tags}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="blog-post-hero">
        <div className="blog-post-hero-bg">
          <img
            src={article.image || PLACEHOLDER_IMAGE}
            alt=""
            onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
          />
          <div className="blog-post-hero-overlay" />
        </div>
        
        <div className="container">
          <motion.div
            className="blog-post-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="blog-post-back-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </Link>
            
            <div className="blog-post-meta-row">
              {article.tags?.length > 0 && (
                <span className="blog-post-category">{article.tags[0]}</span>
              )}
              <span className="blog-post-date">{formatDate(article.created_at)}</span>
              <span className="blog-post-read-time">{formatReadTime(article.content)}</span>
              <span className="blog-post-views">{article.views || 0} views</span>
            </div>
            
            <h1 className="blog-post-title">{article.title}</h1>
            
            {article.excerpt && (
              <p className="blog-post-excerpt">{article.excerpt}</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="blog-post-content-section">
        <div className="container">
          <div className="blog-post-layout">
            {/* Share Bar - Fixed on side */}
            <motion.aside 
              className="blog-post-share-bar"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <span className="blog-share-label">Share</span>
              <div className="blog-share-buttons">
                <ShareButton platform="twitter" url={currentUrl} title={article.title} />
                <ShareButton platform="linkedin" url={currentUrl} title={article.title} />
                <ShareButton platform="facebook" url={currentUrl} title={article.title} />
                <ShareButton platform="whatsapp" url={currentUrl} title={article.title} />
                <button 
                  className={`blog-share-btn blog-share-copy ${copied ? "copied" : ""}`}
                  onClick={copyToClipboard}
                  title="Copy link"
                >
                  {copied ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                  )}
                </button>
              </div>
            </motion.aside>

            {/* Main Content */}
            <motion.article 
              className="blog-post-article"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Slim CTA above the article: most readers arrive from search and never scroll to the end */}
              <div className="blog-inline-cta">
                <p>
                  <strong>Practise while you read.</strong> Free daily tests, past questions and friend challenges in the CampusGenie app.
                </p>
                <div className="blog-inline-cta-links">
                  <a href={APP_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer">iOS</a>
                  <a href={PLAY_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer">Android</a>
                </div>
              </div>

              {article.content?.trim() ? (
                <div className="blog-post-body">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p>{children}</p>,
                      h1: ({ children }) => <h1>{children}</h1>,
                      h2: ({ children }) => <h2>{children}</h2>,
                      h3: ({ children }) => <h3>{children}</h3>,
                      h4: ({ children }) => <h4>{children}</h4>,
                      ul: ({ children }) => <ul>{children}</ul>,
                      ol: ({ children }) => <ol>{children}</ol>,
                      li: ({ children }) => <li>{children}</li>,
                      a: ({ href, children }) => (
                        <a href={href} target="_blank" rel="noopener noreferrer">
                          {children}
                        </a>
                      ),
                      blockquote: ({ children }) => <blockquote>{children}</blockquote>,
                      code: ({ inline, children }) =>
                        inline ? <code>{children}</code> : <pre><code>{children}</code></pre>,
                      img: ({ src, alt }) => (
                        <figure className="blog-post-figure">
                          <img src={src} alt={alt || ""} />
                          {alt && <figcaption>{alt}</figcaption>}
                        </figure>
                      ),
                    }}
                  >
                    {article.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <div className="blog-post-no-content">
                  <p>No content available for this article yet.</p>
                </div>
              )}

              {/* Tags */}
              {article.tags?.length > 0 && (
                <div className="blog-post-tags-section">
                  <span className="blog-post-tags-label">Tags:</span>
                  <div className="blog-post-tags">
                    {article.tags.map((tag) => (
                      <span key={tag} className="blog-post-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Mobile Share */}
              <div className="blog-post-share-mobile">
                <span className="blog-share-label">Share this article</span>
                <div className="blog-share-buttons">
                  <ShareButton platform="twitter" url={currentUrl} title={article.title} />
                  <ShareButton platform="linkedin" url={currentUrl} title={article.title} />
                  <ShareButton platform="facebook" url={currentUrl} title={article.title} />
                  <ShareButton platform="whatsapp" url={currentUrl} title={article.title} />
                  <button 
                    className={`blog-share-btn blog-share-copy ${copied ? "copied" : ""}`}
                    onClick={copyToClipboard}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            </motion.article>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {related.length > 0 && (
        <section className="blog-related-section">
          <div className="container">
            <Reveal>
              <div className="blog-related-header">
                <h2>Continue Reading</h2>
                <Link to="/blog" className="blog-view-all">
                  View All Articles
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </Reveal>

            <div className="blog-related-grid">
              {related.map((entry, index) => (
                <Reveal key={entry.id} delay={index * 0.1}>
                  <Link to={`/blog/${entry.id}`} className="blog-related-card">
                    <div className="blog-related-image">
                      <img
                        src={entry.image || PLACEHOLDER_IMAGE}
                        alt={entry.title}
                        onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                      />
                    </div>
                    <div className="blog-related-content">
                      <span className="blog-related-meta">
                        {formatDate(entry.created_at)} • {formatReadTime(entry.content)}
                      </span>
                      <h3 className="blog-related-title">{entry.title}</h3>
                      <span className="blog-related-link">
                        Read Article
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="blog-post-cta-section">
        <div className="container">
          <Reveal>
            <div className="blog-post-cta">
              <div className="blog-post-cta-content">
                <h3>Turn reading into practice</h3>
                <p>Take a free daily test, challenge a friend head-to-head and keep your streak alive. Free on iOS and Android.</p>
              </div>
              <div className="blog-post-cta-buttons">
                <a href={APP_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                  Download on iOS
                </a>
                <a href={PLAY_STORE_URL} onClick={trackStoreClick} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-lg">
                  Get it on Android
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
